import { User, Wallet } from '@/backend/user/get-user/types';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

/* -------------------------------------------------------------------------- */

interface AppStore {
	user: Omit<User, 'wallets'> | null;
	userWallets: User['wallets'] | null;
	activeWallet: User['wallets'][number] | null;
	avatars: any[] | null;
	randomAvatar: any | null;

	actions: {
		setUser: (user: User) => void;
		setUserWallets: (wallets: User['wallets']) => void;
		updateWallet: (wallet: User['wallets'][number]) => void;
		setActiveWallet: (wallet: Wallet) => void;
		setAvatars: (avatars: any[]) => void;
		setRandomAvatar: (avatar: any) => void;
		resetUserStore: () => void;
	};
}

const initialState = {
	user: null,
	userWallets: null,
	activeWallet: null,
	avatars: null,
	randomAvatar: null,
};

const appStore = create<AppStore>((set) => ({
	...initialState,

	/* -------------------------------------------------------------------------- */

	actions: {
		setUser: (user: User) => {
			set(() => ({ user }));
		},
		setUserWallets: (wallets: User['wallets']) => {
			set(() => ({ userWallets: wallets }));
		},
		setActiveWallet: (wallet: Wallet) => {
			set(() => ({
				activeWallet: wallet,
			}));
		},

		updateWallet: (wallet: User['wallets'][number]) => {
			set((state) => {
				const updatedUserWallets =
					state.userWallets?.map((userWallet) =>
						userWallet.id === wallet.id
							? { ...userWallet, ...wallet }
							: userWallet
					) || [];

				const updatedActiveWallet =
					updatedUserWallets.find((w) => w.id === wallet.id) ||
					state.activeWallet;

				return {
					userWallets: updatedUserWallets,
					activeWallet: updatedActiveWallet,
				};
			});
		},
		setAvatars: (avatars: any[]) => {
			set(() => ({ avatars }));
		},
		setRandomAvatar: (avatar: any) => {
			set(() => ({ randomAvatar: avatar }));
		},
		/* -------------------------------------------------------------------------- */
		resetUserStore: () => {
			set(() => ({
				...initialState,
			}));
		},
	},
}));

export const useUser = () => appStore(useShallow((state) => state.user));
export const useActiveWallet = () =>
	appStore(useShallow((state) => state.activeWallet));
export const useUserWallets = () =>
	appStore(useShallow((state) => state.userWallets));

export const useAvatars = () => appStore((state) => state.avatars);
export const useRandomAvatar = () => appStore((state) => state.randomAvatar);
/* -------------------------------------------------------------------------- */
export const useUserStoreActions = () => appStore((state) => state.actions);
